import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useBooking } from '../../../contexts/BookingContext';

const ServicesPreview = () => {
  const [activeService, setActiveService] = useState('launchpad');
  const { openBooking } = useBooking();

  const services = [
    {
      id: 'launchpad',
      name: 'GCC Launchpad',
      description: 'From blueprint to profit-generating operations in 90 days',
      timeline: '90 days',
      outcome: 'Slash setup costs by 40%',
      icon: 'Zap',
      highlights: [
        'Battle-tested legal frameworks',
        'Pre-negotiated banking partnerships', 
        'Compliance automation systems',
        'Risk-free launch guarantee'
      ],
      pricing: 'Fixed-price clarity'
    },
    {
      id: 'entity',
      name: 'Tech Talent On Tap',
      description: 'Elite developers, data scientists, and AI specialists ready to deploy',
      timeline: 'Immediate',
      outcome: 'Retention above India IT industry average; zero hiring lag',
      icon: 'Users',
      highlights: [
        'Multi-stage SkillStack™ assessment with strict acceptance criteria',
        'Pre-vetted technical specialists',
        'Instant team scaling (up/down)',
        'Performance-guaranteed placements'
      ],
      pricing: 'Pay-per-use flexibility'
    },
    {
      id: 'engagement',
      name: 'Flexible Engagement Models',
      description: 'Scale up, down, or pivot without stranded costs or rigid contracts',
      timeline: 'Adaptive',
      outcome: 'Zero stranded investments',
      icon: 'Handshake',
      highlights: [
        'Company-Owned & Operated',
        'Joint Venture Partnership',
        'Build-Operate-Transfer (BOT)',
        'Virtual GCC (VCC)'
      ],
      pricing: 'Multiple risk profiles'
    },
    {
      id: 'insight',
      name: 'AI Innovation Lab',
      description: 'Experiment with cutting-edge AI agents and workflows risk-free',
      timeline: 'Day 1 access',      icon: 'Brain',
      highlights: [
        'Reusable AI agent library',
        'No-code workflow builders',
        'Innovation ROI tracking',
        'Keep every asset you build'
      ],
      pricing: 'Innovation insurance included'
    }
  ];

  const engagementModels = [
    {
      name: 'Company-Owned & Operated',
      description: 'You own the entity and staff; Bootminds sets it up and hands you the keys',
      bestFor: 'Full control from day one'
    },
    {
      name: 'Joint Venture',
      description: 'We co-invest and run the centre together until you\'re ready to buy us out',
      bestFor: 'Shared risk & local insight'
    },
    {
      name: 'Virtual GCC (VCC)',
      description: 'Your core team is on your payroll; Bootminds runs non-core functions',
      bestFor: 'Light footprint, maximum flexibility'
    },
    {
      name: 'Build-Operate-Transfer (BOT)',
      description: 'Bootminds builds and operates; you assume ownership after steady-state',
      bestFor: '"Done-for-you" start, later take over'
    }
  ];

  const activeServiceData = services.find(service => service.id === activeService);

  return (
    <section className="py-24 lg:py-32 bg-background-alt" data-testid="services-section">
      <div className="container-executive">
        {/* Section Header - pain and solution focused */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-3 bg-destructive/10 text-destructive px-6 py-3 rounded-full text-sm font-body-medium mb-8">
            <Icon name="AlertTriangle" size={16} />
            <span>While competitors scramble, you accelerate</span>
          </div>
          
          <h2 className="font-headline font-headline-bold text-4xl lg:text-6xl text-text-primary mb-8 tracking-tight">
            Turn GCC Chaos Into
            <span className="block text-accent font-light">Competitive Advantage</span>
          </h2>
          
          <p className="text-xl lg:text-2xl text-text-secondary max-w-4xl mx-auto leading-relaxed font-light">
            <span className="text-destructive font-medium">Stop losing months to talent searches and setup delays.</span> 
            Our battle-tested playbooks and elite talent pool get you operational while competitors are still planning.
          </p>
        </div>

        {/* Service Navigation - clean buttons */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => setActiveService(service.id)}
              className={`flex flex-col lg:flex-row items-center justify-center lg:justify-start space-y-2 lg:space-y-0 lg:space-x-3 px-4 lg:px-6 py-4 rounded-2xl transition-all duration-300 text-center lg:text-left ${
                activeService === service.id
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-card text-text-primary border border-border hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                activeService === service.id ? 'bg-primary-foreground/20' : 'bg-primary/10'
              }`}>
                <Icon name={service.icon} size={20} color={activeService === service.id ? "var(--color-primary-foreground)" : "var(--color-primary)"} />
              </div>
              <span className="font-body-medium text-sm lg:text-base">
                {service.name.split('®')[0].split('™')[0]}
              </span>
            </button>
          ))}
        </div>

        {/* Active Service Display - clean layout */}
        <div className="grid lg:grid-cols-2 gap-16 mb-20">
          {/* Service Details */}
          <div className="space-y-8">
            <div className="flex items-center space-x-6 mb-8">
              <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
                <Icon name={activeServiceData?.icon} size={36} color="var(--color-primary-foreground)" />
              </div>
              
              <div>
                <h3 className="font-headline font-headline-bold text-3xl text-text-primary mb-2">
                  {activeServiceData?.name}
                </h3>
                
                <div className="flex items-center space-x-4 mb-3">
                  <span className="bg-success/10 text-success px-4 py-2 rounded-full text-sm font-body-medium">
                    {activeServiceData?.timeline}
                  </span>
                  <span className="text-text-secondary text-sm">
                    {activeServiceData?.pricing}
                  </span>
                </div>
                
                {activeServiceData?.outcome && (
                  <div className="bg-success/5 border border-success/20 rounded-xl p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Target" size={16} color="var(--color-success)" />
                      <span className="text-success font-medium text-sm">
                        Outcome: {activeServiceData.outcome}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-card rounded-2xl p-8 border border-border">
              <p className="text-xl text-text-secondary leading-relaxed">
                {activeServiceData?.description}
              </p>
            </div>

            {/* Service Highlights */}
            <div>
              <h4 className="font-body font-body-medium text-xl text-text-primary mb-6">
                {activeService === 'engagement' ? 'Available Models:' : 'Key Deliverables:'}
              </h4>
              
              {activeService === 'engagement' ? (
                <div className="space-y-4">
                  {engagementModels.map((model, index) => (
                    <div key={index} className="bg-card border border-border rounded-2xl p-6">
                      <div className="flex items-start space-x-4">
                        <Icon name="Check" size={20} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                        <div>
                          <h5 className="font-body-medium text-text-primary mb-2">
                            {model.name}
                          </h5>
                          <p className="text-sm text-text-secondary mb-3 leading-relaxed">
                            {model.description}
                          </p>
                          <span className="text-xs text-accent font-body-medium bg-accent/10 px-3 py-1 rounded-full">
                            Best for: {model.bestFor}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {activeServiceData?.highlights?.map((highlight, index) => (
                    <div key={index} className="flex items-center space-x-4 bg-card border border-border rounded-2xl p-5">
                      <Icon name="Check" size={18} color="var(--color-success)" className="flex-shrink-0" />
                      <span className="text-text-secondary">
                        {highlight}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Value Proposition - clean card */}
          <div className="bg-card border border-border rounded-3xl p-10">
            <h4 className="font-headline font-headline-bold text-2xl text-text-primary mb-8">
              Why CXOs Choose Bootminds
            </h4>
            
            <div className="space-y-8">
              {[
                {
                  icon: 'Zap',
                  title: 'Speed with Certainty',
                  content: 'India-ready in 12-20 weeks, governed by milestone SLAs'
                },
                {
                  icon: 'DollarSign',
                  title: 'Transparent Economics',
                  content: 'Open rate card, fixed fees, no hidden "India complexity" mark-ups'
                },
                {
                  icon: 'Shield',
                  title: 'Big-4-grade Controls',
                  content: 'KPMG-benchmarked governance and compliance from Day 1'
                },
                {
                  icon: 'Brain',
                  title: 'AI-Driven Decisions',
                  content: 'Every metric surfaced in a single executive cockpit'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-5">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name={item.icon} size={20} color="var(--color-primary-foreground)" />
                  </div>
                  
                  <div>
                    <h5 className="font-body-medium text-text-primary mb-2">
                      {item.title}
                    </h5>
                    <p className="text-sm text-text-secondary leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-8 mt-8 border-t border-border">
              <div className="bg-muted/50 p-6 rounded-2xl mb-8 text-center">
                <div className="text-2xl font-headline font-headline-bold text-text-primary mb-2">
                  50–60% cost savings vs US operations
                </div>
                <div className="text-xs text-text-secondary">Source: Zinnov GCC Cost Index 2024</div>
              </div>

              <Button
                variant="primary"
                size="lg"
                fullWidth
                className="bg-cta text-cta-foreground hover:bg-cta/90"
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}
                onClick={() => openBooking('services_preview')}
              >
                Book 30-Min Executive Demo
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom CTA - clean and simple */}
        <div className="text-center">
          <div className="bg-card border border-border rounded-3xl p-12 max-w-3xl mx-auto">
            <h3 className="font-headline font-headline-bold text-2xl text-text-primary mb-6">
              See All Bootminds Solutions
            </h3>
            
            <p className="text-text-secondary mb-8 leading-relaxed">
              Explore our complete portfolio of GCC establishment services, engagement models, 
              and AI-powered intelligence platform with detailed specifications and transparent pricing.
            </p>
            
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-primary text-primary hover:bg-primary/5"
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={20}
              asChild
            >
              <Link to="/services">
                View Complete Service Portfolio
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;